import React from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Tooltip,
  makeStyles,
} from "@fluentui/react-components";
import { Report, ReportParameter } from "../../../types/reportTypes";
import FormField from "./FormField";
import PopupTable from "./PopupTable";
import SavedReportsPanel from "./SavedReportsPanel";
import { rowIdentityOf } from "../rowIdentity";
import { useReportForm } from "../hooks/useReportForm";
import {
  SAVED_REPORT_NAME_MAX_LENGTH,
  SAVED_REPORTS_LIMIT,
  SavedReport,
  useSavedReports,
} from "../hooks/useSavedReports";
import { downloadReport } from "../serviceFile";
import { transformReportName } from "../../../utils";
import { setDialogModalOptions } from "../../../redux/modelDialogReducer";
import moment from "moment";
import "../style.css";

const useStyles = makeStyles({
  formField: {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    gap: "10px",
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "end",
  },
  // The mockup's `.form-actions`: the save-row on the left, Clear/Generate
  // pushed to the right.
  actionsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    columnGap: "16px",
    rowGap: "12px",
    flexWrap: "wrap",
  },
  saveRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
  },
  saveNameInput: { minWidth: "240px" },
  // 3-columns-per-row parameter grid, wrapping to further rows and stepping
  // down to 2/1 columns on narrower viewports.
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    columnGap: "20px",
    rowGap: "16px",
    alignItems: "start",
    "@media (max-width: 860px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  fieldFullWidth: {
    gridColumnStart: "1",
    gridColumnEnd: "-1",
  },
});

interface ReportFormProps {
  selectedReport: Report;
  onReportGenerated: () => void;
  showToast: (title: string, message: string, type: string | undefined) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  // Values merged into the submit payload without ever being rendered as a
  // field — e.g. the client-context Reports screen injects `customerId` (and
  // `startDate`/`endDate` when the report doesn't declare its own) so the
  // "Select Client" popup never has to appear. Omitted on the standalone
  // /report page, so its behavior is unchanged.
  fixedParams?: Record<string, any>;
  // Renders a "Clear" button alongside "Generate Report" when provided.
  // Omitted on the client-context Reports screen, which has no Clear button.
  onClear?: () => void;
  // When provided, the Clear/Generate buttons are portalled into this element
  // instead of rendering below the fields — the client-context Reports screen
  // uses it to put Generate on the same row as its "Select a Report" dropdown.
  actionsContainer?: HTMLElement | null;
  // Last name for the download filename when the client isn't picked in a
  // "Client List" field — the client-context Reports screen, which strips that
  // field. A Client List selection, when present, takes precedence.
  clientLastName?: string;
}

// Helper function to safely get nested values from objects
const getNestedValue = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

// Rebuilds a popup table's row selection from the value already committed to
// the form. handleClosePopup deliberately clears the popup's own selection
// state (so cancelling discards an uncommitted choice), which left a reopened
// popup showing an empty table even though the field displayed a selection —
// this puts the committed choice back when the popup is opened again.
//
// Rows are keyed by the record's OWN identifier, matching PopupTable's
// getRowId. Two consequences, both deliberate:
//
//   * The selection no longer depends on what page is loaded. A record saved
//     on page 2 highlights correctly as soon as page 2 is shown, and is never
//     confused with whatever sits at the same index on another page.
//   * Ids for records on pages that haven't been fetched are still written
//     into the selection map. MRT ignores keys with no matching row, so they
//     are harmless — and they mean paging to that record lights it up without
//     any extra work.
const restoreSelectionFromForm = (
  param: ReportParameter | undefined,
  committedValue: any,
): { rowSelection: { [key: string]: boolean }; selected: any } => {
  if (!committedValue) return { rowSelection: {}, selected: null };

  const rowSelection: { [key: string]: boolean } = {};
  (Array.isArray(committedValue) ? committedValue : [committedValue])
    .map((item) => rowIdentityOf(param, item))
    .filter((id) => id !== undefined && id !== null && typeof id !== "object")
    .forEach((id) => {
      rowSelection[String(id)] = true;
    });

  return { rowSelection, selected: committedValue };
};

// Whether a parameter's form value counts as filled in.
const hasParamValue = (param: ReportParameter, value: any): boolean => {
  if (param.multiSelect) return Array.isArray(value) && value.length > 0;
  if (param.category === "daterange") {
    return Boolean(value?.startDate && value?.endDate);
  }
  return Boolean(value);
};

// For now, Client List and date ranges do not have to be filled in to Save as
// My Reports, even when the report marks them required. Generate Report still
// requires them.
const isOptionalForSave = (param: ReportParameter): boolean =>
  param.reportParam === "customerId" || param.category === "daterange";

const ReportForm: React.FC<ReportFormProps> = ({
  selectedReport,
  onReportGenerated,
  showToast,
  setIsLoading,
  fixedParams,
  onClear,
  actionsContainer,
  clientLastName,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const {
    formValues,
    errors,
    popupState,
    dropdownOptions,
    loadingFields,
    handleInputChange,
    handleBlur,
    loadReportParams,
    validateAll,
    setPopupState,
    onLoadMore,
  } = useReportForm(selectedReport);

  // "My Reports" is only part of the standalone /report page, whose mockup it
  // comes from. fixedParams is passed exclusively by the client-context Reports
  // screen, which has its own layout (no Clear button, its own always-on date
  // fields) and whose saved parameter sets would be client-specific — so that
  // screen is left byte-for-byte unchanged, exactly as it is for onClear.
  const savedReportsEnabled = !fixedParams;

  const savedReports = useSavedReports(
    savedReportsEnabled ? selectedReport.ReportCode : undefined,
  );
  const [saveName, setSaveName] = React.useState("");
  const [saveError, setSaveError] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  // Reset the save-row whenever the selected report changes — the name belongs
  // to one report's saved set, and the panel below has already reloaded.
  React.useEffect(() => {
    setSaveName("");
    setSaveError("");
  }, [selectedReport.ReportCode]);

  // Same rules and messages as the backend's SavedReportParamName validation.
  // Checked against the trimmed value, since that is what useSavedReports.save
  // sends.
  const validateSaveNameFormat = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) return "Saved ReportParam Name cannot be empty";
    if (trimmed.length > SAVED_REPORT_NAME_MAX_LENGTH)
      return `Saved ReportParam Name cannot exceed ${SAVED_REPORT_NAME_MAX_LENGTH} characters`;
    return "";
  };

  const validateSaveName = (name: string): string => {
    const formatError = validateSaveNameFormat(name);
    if (formatError) return formatError;
    const trimmed = name.trim();
    // A UX shortcut on top of, not instead of, the backend's own duplicate
    // check — the in-memory list can be stale if another tab or device saved
    // the same name since this page loaded, so handleSaveReport still handles a
    // 409 from the server.
    if (savedReports.isDuplicateName(trimmed))
      return "A saved report with this name already exists";
    return "";
  };

  const handleSaveReport = async () => {
    // Same per-field validation Generate Report runs, except that Client List
    // and date ranges may be left empty (see isOptionalForSave). A saved set
    // that fails field validation (an invalid date range, say) would restore
    // into a broken form. validateAll also writes the errors to state, so they
    // are shown, not just checked.
    if (!validateAll(isOptionalForSave)) {
      return;
    }

    const validationError = validateSaveName(saveName);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    // Store the generation payload itself — the same `{ reportParam: value }`
    // object Generate Report posts (e.g. { customerId: "4fa8ab21-..." }) — not
    // the raw per-field form state. A saved row is therefore directly runnable
    // as-is, without the form having to re-derive it.
    const { params, configErrorMessage } = buildReportParams();
    if (configErrorMessage) {
      setSaveError(configErrorMessage);
      showToast("Report configuration error", configErrorMessage, "error");
      return;
    }

    try {
      setIsSaving(true);
      setSaveError("");
      await savedReports.save(saveName, params);
      setSaveName("");
      showToast("Saved to My Reports", "", "success");
    } catch (error: any) {
      // The backend stays authoritative for duplicate-name and limit-reached;
      // surface its own message rather than a generic one.
      const backendMessage = error?.response?.data?.error?.message;
      const message = Array.isArray(backendMessage)
        ? backendMessage.join(", ")
        : backendMessage;
      setSaveError(message || "Failed to save this report");
      showToast("Failed to save", message || "Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadSavedReport = (row: SavedReport) => {
    // ReportParams holds the generation payload, so this is the inverse of
    // buildReportParams — see loadReportParams. No JSON.parse: the column is
    // jsonb, so it arrives as a real object.
    //
    // Passed through as-is: loadReportParams owns the "no usable values" case
    // (undefined / null / "" / any non-object), and renders every field empty
    // rather than leaving the previous report's values on screen.
    loadReportParams(row.ReportParams);
    setSaveName(row.SavedReportParamName);
    setSaveError("");
  };

  // One click on the row's delete icon used to remove the saved report
  // immediately — no confirmation, no undo, in a row where clicking anywhere
  // ELSE loads it instead. This asks first, using the app's existing
  // confirmation dialog (ModalDialog, driven via the `dialog` redux slice —
  // already mounted once in the layout shell, so dispatching options here is
  // all that is needed). Matches this codebase's own delete-confirmation
  // convention elsewhere: `no` is the safe default (primary-styled "No"),
  // `cancel` is the actual confirm action (labelled "Yes").
  const confirmDeleteSavedReport = (row: SavedReport) => {
    const closeDialog = () => dispatch(setDialogModalOptions({ open: false }));

    dispatch(
      setDialogModalOptions({
        open: true,
        content_line_1: `Do you want to delete saved report "${row.SavedReportParamName}"?`,
        no: {
          onclick: closeDialog,
          label: "No",
          type: "primary",
        },
        cancel: {
          label: "Yes",
          onclick: async () => {
            closeDialog();
            setDeletingId(row.SavedReportParamId);
            try {
              await savedReports.remove(row.SavedReportParamId);
              showToast("Removed from My Reports", "", "success");
            } catch (error) {
              console.error("Failed to delete saved report:", error);
              showToast("Failed to delete", "Please try again.", "error");
            } finally {
              setDeletingId(null);
            }
          },
        },
      }),
    );
  };

  // The single source of truth for the report-generation payload — the
  // `{ reportParam: value }` object POSTed to generate the report.
  //
  // Extracted out of handleSubmit so "Save as My Reports" stores exactly the
  // payload that generating would send, rather than a second, separately
  // maintained transform that could drift from it.
  const buildReportParams = (): {
    params: Record<string, any>;
    selectedClientLastName: string;
    configErrorMessage: string | null;
  } => {
    const params: Record<string, any> = {};
    let selectedClientLastName = "";
    let configErrorMessage: string | null = null;

    selectedReport.parameters.forEach((param) => {
      const value = formValues[param.label];
      if (!value) return;

      let finalValue = value;

      if (param.category === "select") {
        if (param.popup) {
          if (param.multiSelect) {
            // Handle multi-select popup
            if (param.apiEndpoint!.value.includes(".")) {
              finalValue = Array.isArray(value)
                ? value.map((item: any) =>
                    getNestedValue(item, param.apiEndpoint!.value),
                  )
                : value
                  ? [getNestedValue(value, param.apiEndpoint!.value)]
                  : [];
            } else {
              finalValue = Array.isArray(value)
                ? value.map((item: any) => item[param.apiEndpoint!.value])
                : value
                  ? [value[param.apiEndpoint!.value]]
                  : [];
            }
          } else {
            // Handle single select popup
            if (param.apiEndpoint!.value.includes(".")) {
              finalValue =
                getNestedValue(value, param.apiEndpoint!.value) || "";
            } else {
              finalValue = value?.[param.apiEndpoint!.value] || "";
            }
          }
          if (param.label === "Client List") {
            selectedClientLastName = value?.last_name || "";
          }
        } else {
          // Handle regular dropdown
          if (param.multiSelect) {
            finalValue = Array.isArray(value)
              ? value.map((item: any) => item.value)
              : value?.value
                ? [value.value]
                : [];
          } else {
            finalValue = value?.value || "";
          }
        }
      } else if (param.category === "datepicker") {
        finalValue = value;
      } else if (param.category === "daterange") {
        // Skip incomplete optional ranges instead of sending empty-string
        // dates — matches how every other optional field is treated above.
        if (!value?.startDate || !value?.endDate) return;

        const [startKey, endKey] = (param.reportParam || "")
          .split(",")
          .map((k) => k.trim());
        if (!startKey || !endKey) {
          console.error(
            `Report parameter "${param.label}" has category "daterange" but reportParam is not a valid "startKey,endKey" pair: "${param.reportParam}"`,
          );
          configErrorMessage = `"${param.label}" is misconfigured and could not be included in the report. Please contact support.`;
          return;
        }
        params[startKey] = value.startDate;
        params[endKey] = value.endDate;
        return;
      }

      const key = param.reportParam;
      if (key) {
        params[key] = finalValue;
      }
    });

    // Merge in values that were never rendered as fields at all (e.g.
    // customerId/startDate/endDate supplied by the client-context Reports
    // screen). No-op when fixedParams is undefined, so the standalone
    // /report page's payload is byte-for-byte unchanged.
    Object.assign(params, fixedParams);

    return { params, selectedClientLastName, configErrorMessage };
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      return;
    }

    const { params, selectedClientLastName, configErrorMessage } =
      buildReportParams();

    if (configErrorMessage) {
      showToast("Report configuration error", configErrorMessage, "error");
      return;
    }

    try {
      setIsLoading(true);
      const blob = await downloadReport(
        selectedReport.ReportCode,
        selectedReport.ContentType as "pdf" | "xlsx" | "docx",
        selectedReport.scope,
        params,
      );

      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const extension =
          selectedReport.ContentType === "pdf"
            ? "pdf"
            : selectedReport.ContentType === "docx"
              ? "docx"
              : "xlsx";

        // Date_LastName_ReportName, e.g.
        // 20261103123333_Joe_AssetListingDetailed.pdf. The last name is
        // dropped when there is no client.
        const lastName = (selectedClientLastName || clientLastName || "").trim();
        link.download = `${[
          moment().format("YYYYMMDDHHmmss"),
          lastName,
          transformReportName(selectedReport.Name),
        ]
          .filter(Boolean)
          .join("_")}.${extension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast("Report generated successfully!", "", "success");
        setIsLoading(false);
        onReportGenerated();
      } else {
        showToast("Failed to download the report.", "", "error");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      showToast("An error occurred while downloading the report.", "", "error");
    }
  };

  const handleOpenPopup = (paramLabel: string) => {
    const param = selectedReport.parameters.find((p) => p.label === paramLabel);
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        isOpen: true,
        ...restoreSelectionFromForm(param, formValues[paramLabel]),
      },
    }));
  };

  const handleClosePopup = (paramLabel: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        isOpen: false,
        // The search query and page are deliberately KEPT here (the Select
        // path; Cancel clears them in handleCancelPopup). Reopening lands on
        // the same filtered page the selection was made from, so the committed
        // record is on screen with its radio restored (by handleOpenPopup,
        // from the committed form value) rather than lost behind an unfiltered
        // first page.
        //
        // This is safe only because the search box renders expanded
        // (`showGlobalFilter` in PopupTable's initialState). MRT disables its
        // show/hide-search toggle whenever a filter is set
        // (`disabled: !!globalFilter`), so with the box collapsed a retained
        // query would be unreachable — no way to clear it or search again.
        // Expanded, the field carries its own clear (X) control.
        rowSelection: {},
        selected: null,
      },
    }));
  };

  // Cancel (or Escape / click outside) discards a search typed in the popup —
  // it is only kept, per the note in handleClosePopup, once a row is Selected.
  // Back to page 1 too, since the page belonged to that search. Clearing the
  // query also makes useReportForm refetch the unfiltered list.
  const handleCancelPopup = (paramLabel: string) => {
    handleClosePopup(paramLabel);
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        searchQuery: "",
        pagination: {
          ...prev[paramLabel]?.pagination,
          pageIndex: 0,
        },
      },
    }));
  };

  const handleSelectFromPopup = (paramLabel: string, selected: any) => {
    handleInputChange(paramLabel, selected);
    handleClosePopup(paramLabel);
  };

  const handleRowSelectionChange = (paramLabel: string, updater: any) => {
    setPopupState((prev: any) => {
      const currentData = prev[paramLabel]?.data || [];
      const currentSelection = prev[paramLabel]?.rowSelection || {};
      const newRowSelection =
        typeof updater === "function" ? updater(currentSelection) : updater;
      let selectedItems = null;

      const param = selectedReport.parameters.find(
        (p) => p.label === paramLabel,
      );

      // Selection keys are record identifiers now, not indices into the
      // current page (see restoreSelectionFromForm / PopupTable's getRowId).
      // Resolve them against the loaded page FIRST, then against what was
      // already selected — so a multi-select choice made on page 1 survives
      // paging to page 2, where those rows are no longer in `currentData`.
      const previous = prev[paramLabel]?.selected;
      const previousList = Array.isArray(previous)
        ? previous
        : previous
          ? [previous]
          : [];
      const pool = [...currentData, ...previousList];
      const findById = (id: string) =>
        pool.find((row: any) => String(rowIdentityOf(param, row)) === id);

      const selectedIds = Object.keys(newRowSelection).filter(
        (key) => newRowSelection[key],
      );

      if (param?.multiSelect) {
        selectedItems = selectedIds.map(findById).filter(Boolean);
      } else {
        selectedItems = selectedIds.length ? (findById(selectedIds[0]) ?? null) : null;
      }

      return {
        ...prev,
        [paramLabel]: {
          ...prev[paramLabel],
          rowSelection: newRowSelection,
          selected: selectedItems,
        },
      };
    });
  };

  const handlePaginationChange = (paramLabel: string, updater: any) => {
    const newPagination =
      typeof updater === "function"
        ? updater(
            popupState[paramLabel]?.pagination || {
              pageIndex: 0,
              pageSize: 10,
            },
          )
        : updater;
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        pagination: newPagination,
        isOpen: true,
      },
    }));
  };

  // Mirrors handleGlobalFilterChange: a new sort order means page 1 of that
  // order, not "page 5 of the old order".
  const handleSortingChange = (paramLabel: string, updater: any) => {
    const newSorting =
      typeof updater === "function"
        ? updater(popupState[paramLabel]?.sorting || [])
        : updater;
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        sorting: newSorting,
        pagination: {
          ...prev[paramLabel]?.pagination,
          pageIndex: 0,
        },
      },
    }));
  };

  const handleGlobalFilterChange = (paramLabel: string, value: string) => {
    setPopupState((prev) => ({
      ...prev,
      [paramLabel]: {
        ...prev[paramLabel],
        searchQuery: value || "",
        pagination: {
          ...prev[paramLabel]?.pagination,
          pageIndex: 0,
        },
      },
    }));
  };
  const isFormValid = React.useMemo(() => {
    // If any error exists, form is invalid
    if (Object.values(errors).some((msg) => msg)) return false;

    // Ensure required fields are filled
    return selectedReport.parameters.every(
      (param) =>
        !param.required || hasParamValue(param, formValues[param.label]),
    );
  }, [errors, formValues, selectedReport.parameters]);

  // Save as My Reports: like isFormValid, but Client List and date ranges may
  // be empty, and at least one field must be filled so an empty preset (e.g. a
  // report whose only field is an empty Client List) can't be saved.
  const isSaveFormValid = React.useMemo(() => {
    if (Object.values(errors).some((msg) => msg)) return false;

    const { parameters } = selectedReport;
    const requiredFilled = parameters.every(
      (param) =>
        !param.required ||
        isOptionalForSave(param) ||
        hasParamValue(param, formValues[param.label]),
    );
    const anyFilled =
      parameters.length === 0 ||
      parameters.some((param) => hasParamValue(param, formValues[param.label]));
    return requiredFilled && anyFilled;
  }, [errors, formValues, selectedReport]);

  const actionButtons = (
    <div
      className={styles.buttonContainer}
      style={{
        alignItems: Object.values(errors).every((msg) => !msg)
          ? "end"
          : "center",
      }}
    >
      {onClear && (
        <Button
          appearance="secondary"
          onClick={onClear}
          className="asc-button-secondary"
        >
          Clear
        </Button>
      )}
      <Button
        appearance="primary"
        onClick={handleSubmit}
        className="asc-button-primary"
        disabled={!isFormValid}
      >
        Generate Report
      </Button>
    </div>
  );

  return (
    <div className="report_selection">
      <div className={styles.fieldGrid} data-testid="report-field-grid">
        {selectedReport.parameters.length === 0
          ? // fixedParams is only ever passed by the client-context Reports
            // screen, which already renders its own always-on Start/End Date
            // fields above this form — showing "no additional parameters"
            // there would contradict what's visibly on screen. The standalone
            // /report page never passes fixedParams, so this note still shows
            // there exactly as before.
            !fixedParams && (
              <MessageBar
                className={styles.fieldFullWidth}
                data-testid="report-field-empty-note"
              >
                <MessageBarBody>
                  This report has no additional parameters — just review and
                  generate.
                </MessageBarBody>
              </MessageBar>
            )
          : selectedReport.parameters.map((param) => (
              <div
                key={param.label}
                className={
                  param.category === "textarea"
                    ? styles.fieldFullWidth
                    : undefined
                }
                data-testid={`report-field-cell-${param.label}`}
                data-full-width={
                  param.category === "textarea" ? "true" : undefined
                }
              >
                <FormField
                  param={param}
                  value={formValues[param.label]}
                  error={errors[param.label]}
                  loading={loadingFields[param.label]}
                  dropdownOptions={dropdownOptions[param.label] || []}
                  onInputChange={handleInputChange}
                  onBlur={handleBlur}
                  onOpenPopup={handleOpenPopup}
                />
                {param.popup && (
                  <PopupTable
                    param={param}
                    popupState={
                      popupState[param.label] || {
                        data: [],
                        selected: null,
                        total: 0,
                        isOpen: false,
                        pagination: {
                          pageIndex: 0,
                          pageSize: param.apiEndpoint?.QueryParams?.includes(
                            "offset",
                          )
                            ? 20
                            : 10,
                        },
                        searchQuery: "",
                        rowSelection: {},
                        isFetchingMore: false,
                        sorting: [],
                      }
                    }
                    onClose={() => handleClosePopup(param.label)}
                    onCancel={() => handleCancelPopup(param.label)}
                    onSelect={(selected) =>
                      handleSelectFromPopup(param.label, selected)
                    }
                    onRowSelectionChange={(updater) =>
                      handleRowSelectionChange(param.label, updater)
                    }
                    onPaginationChange={(updater) =>
                      handlePaginationChange(param.label, updater)
                    }
                    onSortingChange={(updater) =>
                      handleSortingChange(param.label, updater)
                    }
                    onGlobalFilterChange={(value) =>
                      handleGlobalFilterChange(param.label, value)
                    }
                    useScrollPagination={
                      param.apiEndpoint?.QueryParams?.includes("offset") ??
                      false
                    }
                    onLoadMore={() => onLoadMore(param.label)}
                    loading={loadingFields[param.label]}
                  />
                )}
              </div>
            ))}
      </div>
      {/* With the buttons portalled out and no save-row, this row would be
          empty, so it is skipped. */}
      {(savedReportsEnabled || !actionsContainer) && (
        <div className={styles.actionsRow}>
          {savedReportsEnabled && (
            <div className={styles.saveRow} data-testid="save-my-report-row">
              <Field
                validationState={saveError ? "error" : "none"}
                validationMessage={saveError || undefined}
              >
                <Input
                  className={styles.saveNameInput}
                  value={saveName}
                  placeholder="Saved Names, e.g. Q2 2026"
                  onChange={(_, data) => {
                    setSaveName(data.value);
                    // Too-long is flagged while typing, since the disabled Save
                    // button would otherwise give no reason. Empty waits for blur
                    // so a freshly cleared field isn't shown as an error.
                    const formatError = validateSaveNameFormat(data.value);
                    setSaveError(data.value.trim() ? formatError : "");
                  }}
                  onBlur={() => setSaveError(validateSaveName(saveName))}
                  data-testid="save-my-report-name"
                />
              </Field>
              {/*
              Only the SAVE action is capped at the limit. "Generate Report"
              runs whatever is currently on the form and is unrelated to how
              many presets are saved — disabling it would block a core,
              unrelated function.
            */}
              <Tooltip
                content={
                  savedReports.isAtLimit
                    ? `Limit reached — delete an existing saved report to add a new one (max ${SAVED_REPORTS_LIMIT})`
                    : "Save the current parameters under a name"
                }
                relationship="label"
              >
                <Button
                  appearance="secondary"
                  className="asc-button-secondary"
                  onClick={handleSaveReport}
                  disabled={
                    savedReports.isAtLimit ||
                    isSaving ||
                    !!validateSaveNameFormat(saveName) ||
                    !isSaveFormValid
                  }
                  data-testid="save-my-report-button"
                >
                  Save as My Reports
                </Button>
              </Tooltip>
            </div>
          )}

          {!actionsContainer && actionButtons}
        </div>
      )}
      {actionsContainer && createPortal(actionButtons, actionsContainer)}

      {savedReportsEnabled && (
        <SavedReportsPanel
          list={savedReports.list}
          total={savedReports.total}
          isLoading={savedReports.isLoading}
          error={savedReports.error}
          deletingId={deletingId}
          onLoadSavedReport={handleLoadSavedReport}
          onDeleteSavedReport={confirmDeleteSavedReport}
        />
      )}
    </div>
  );
};

export default ReportForm;
